import { Link } from '@tanstack/react-router';

import {
  Bell, Building2, ChevronRight, ChevronsUpDown, CreditCard, HelpCircle, MapPin, MessageSquareText,
  Moon, ShieldCheck, Store, Sun, Tag, User, Users,
} from 'lucide-react';

import { Button, cn } from '@src/components/ui';
import { usePermissions, useTheme } from '@src/context/hooks';
import { useOptionLabels } from '@src/query';


function HubRow({ to, icon: Icon, label, value, last }) {
  return (
    <Link
      className={cn(
        'flex items-center gap-3 px-3.5 py-3',
        !last && 'border-b border-hairline',
      )}
      to={to}
    >
      <span className="grid size-[30px] shrink-0 place-items-center rounded-lg bg-primary-soft">
        <Icon className="size-4 text-primary" />
      </span>

      <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{label}</span>

      {value
        ? <span className="text-xs text-ink-subtle">{value}</span>
        : null}

      <ChevronRight className="size-4 shrink-0 text-ink-subtle" />
    </Link>
  );
}

HubRow.displayName = 'HubRow';


function Group({ label, children }) {
  return (
    <>
      <span className="px-1 text-[10.5px] font-semibold uppercase tracking-[0.4px] text-ink-subtle">
        {label}
      </span>

      <div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
        {children}
      </div>
    </>
  );
}

Group.displayName = 'Group';


/** Mobile settings hub (#143, .pen yKDGF): org card + grouped navigable list.
 * Only the tabs shipping in M9 slice 1 appear; the rest land later. The
 * Organization group is hidden for Lister/Viewer (no Business/Team access). */
function MobileHub({ membership, roleLabel }) {
  const theme = useTheme();
  const { canManageOrg, isOwner } = usePermissions();
  const planLabels = useOptionLabels('subscription_plan');
  const seller = membership?.seller;
  const role = membership?.role;
  const memberCount = seller?.team_count;
  const planLabel = (subscription) => {
    const plan = subscription?.plan;
    return planLabels[plan] || (plan ? `${plan[0].toUpperCase()}${plan.slice(1)}` : 'Free');
  };

  return (
    <div className="flex flex-col gap-2.5 p-4">
      {/* Org card */}
      <div className="flex items-center gap-3 rounded-lg border border-hairline bg-surface-1 p-3">
        <span
          className={cn(
            'bg-brand-gradient grid size-[38px] shrink-0 place-items-center rounded-lg',
            'text-card-title font-bold text-white',
          )}
        >
          {(seller?.name || '?').charAt(0).toUpperCase()}
        </span>

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-ink">{seller?.name}</span>

          <span className="text-xs text-ink-subtle">
            {`${roleLabel(role)} · ${planLabel(seller?.subscription)} plan`}
          </span>
        </span>

        <ChevronsUpDown className="size-[15px] shrink-0 text-ink-subtle" />
      </div>

      <Group label="Account">
        <HubRow last label="Profile" icon={User} to="/app/settings/profile" />
      </Group>

      {canManageOrg
        ? (
          <Group label="Organization">
            <HubRow label="Business" icon={Building2} to="/app/settings/business" />

            <HubRow
              label="Team"
              value={memberCount ? String(memberCount) : undefined}
              icon={Users}
              to="/app/settings/team"
            />

            <HubRow label="Channels" icon={Tag} to="/app/settings/channels" />
            <HubRow label="Storefront" icon={Store} to="/app/settings/storefront" />
            <HubRow label="Storefront FAQ" icon={HelpCircle} to="/app/settings/storefront-faq" />
            <HubRow label="Saved Replies" icon={MessageSquareText} to="/app/settings/saved-replies" />

            <HubRow
              label="Locations"
              icon={MapPin}
              to="/app/settings/locations"
            />

            {isOwner
              ? <HubRow label="Billing" icon={CreditCard} to="/app/settings/billing" />
              : null}

            {isOwner
              ? <HubRow last label="Security" icon={ShieldCheck} to="/app/settings/security" />
              : null}
          </Group>
        )
        : null}

      <span className="px-1 text-[10.5px] font-semibold uppercase tracking-[0.4px] text-ink-subtle">
        Preferences
      </span>

      <div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
        <HubRow last label="Notifications" icon={Bell} to="/app/settings/notifications" />
      </div>

      <Button
        type="button"
        variant="ghost"
        className={cn(
          'h-auto justify-start gap-3 rounded-lg border border-hairline',
          'bg-surface-1 px-3.5 py-3 hover:bg-surface-1',
        )}
        onClick={theme?.toggle}
      >
        <span className="grid size-[30px] shrink-0 place-items-center rounded-lg bg-primary-soft">
          {theme?.isLight
            ? <Sun className="size-4 text-primary" />
            : <Moon className="size-4 text-primary" />}
        </span>

        <span className="min-w-0 flex-1 text-left text-sm font-medium text-ink">Theme</span>
        <span className="text-xs text-ink-subtle">{theme?.isLight ? 'Light' : 'Dark'}</span>
      </Button>

      <Link
        className={cn(
          'mt-1 flex h-[46px] items-center justify-center rounded-lg border border-hairline-strong',
          'bg-surface-1 text-sm font-semibold text-danger',
        )}
        to="/auth/logout"
      >
        Sign out
      </Link>
    </div>
  );
}

MobileHub.displayName = 'MobileHub';
export default MobileHub;
